import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    DateRangeValue,
    DeliveryPlanningFilters,
    EMPTY_DELIVERY_PLANNING_FILTERS,
} from '../components/DeliveryPlanningFilterBar';

const FILTER_KEYS = ['name', 'deliveryTypes', 'isSecured', 'hasSensei', 'launchDateRange', 'removalDateRange'] as const;

const parseDateRange = (values: string[]): DateRangeValue | undefined => {
    const range: DateRangeValue = {};
    for (const v of values) {
        const [op, ts] = v.split(':');
        const parsed = parseFloat(ts);
        if ((op === 'gte' || op === 'lte') && !Number.isNaN(parsed)) {
            range[op] = parsed;
        }
    }
    return Object.keys(range).length > 0 ? range : undefined;
};

const parseFiltersFromParams = (params: URLSearchParams): DeliveryPlanningFilters => {
    const filters: DeliveryPlanningFilters = { ...EMPTY_DELIVERY_PLANNING_FILTERS };

    if (params.has('name')) filters.name = params.get('name') ?? '';
    const deliveryTypes = params.getAll('deliveryTypes');
    if (deliveryTypes.length > 0) filters.deliveryTypes = deliveryTypes;
    if (params.has('isSecured')) filters.isSecured = params.get('isSecured') === 'true';
    if (params.has('hasSensei')) filters.hasSensei = params.get('hasSensei') === 'true';

    const launchRange = parseDateRange(params.getAll('launchDateRange'));
    if (launchRange) filters.launchDateRange = launchRange;
    const removalRange = parseDateRange(params.getAll('removalDateRange'));
    if (removalRange) filters.removalDateRange = removalRange;

    return filters;
};

const writeFiltersToParams = (filters: DeliveryPlanningFilters, base: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams(base);
    for (const key of FILTER_KEYS) next.delete(key);

    if (filters.name.trim()) next.set('name', filters.name);
    for (const v of filters.deliveryTypes) next.append('deliveryTypes', v);
    if (filters.isSecured !== undefined) next.set('isSecured', String(filters.isSecured));
    if (filters.hasSensei !== undefined) next.set('hasSensei', String(filters.hasSensei));

    for (const op of ['gte', 'lte'] as const) {
        const v = filters.launchDateRange?.[op];
        if (v != null) next.append('launchDateRange', `${op}:${v}`);
    }
    for (const op of ['gte', 'lte'] as const) {
        const v = filters.removalDateRange?.[op];
        if (v != null) next.append('removalDateRange', `${op}:${v}`);
    }

    return next;
};

export const useDeliveryPlanningFiltersUrlSync = (): [
    DeliveryPlanningFilters,
    (next: DeliveryPlanningFilters) => void,
] => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Local state is the source of truth so controlled inputs stay in sync with keystrokes,
    // even though react-router v7 defers setSearchParams updates. URL is written via effect.
    const [filters, setFilters] = useState<DeliveryPlanningFilters>(() => parseFiltersFromParams(searchParams));

    useEffect(() => {
        setSearchParams((prev) => writeFiltersToParams(filters, prev), { replace: true });
    }, [filters, setSearchParams]);

    const updateFilters = useCallback((next: DeliveryPlanningFilters) => setFilters(next), []);

    return [filters, updateFilters];
};
